import type { StaffRole } from '@/types/wire';

/**
 * Every string here is quoted verbatim from docs/handoff/COPY_DECK.md (O01, O02, O03, O04, O08, O05, O06 "Draft", O07,
 * P02 "Email", P06 "Sign in").
 * Templates keep the deck's punctuation (`·`, `–`, `▾`, `▸`) and only substitute the sample values.
 * Strings the deck does not have live in ./placeholders.ts.
 */
/** "1 branch" / "3 branches": the deck's counts are sample data, the live one can be 1 (cf. o03.reps). */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** "22 children" / "1 child": the count the deck draws in O03's publish line and as an O05 meta segment. */
const childCount = (n: number) => plural(n, 'child', 'children');

export const deck = {
  /**
   * **The clubs portal's language switch** (Spanish for 3.0.11; Evan, 28 Sep: 'an "English · Español" switch at the
   * bottom of every page'). Each language by its own name — never translated. The accessible label is PROPOSED
   * (COPY_DECK's Spanish section; the Spanish in docs/handoff/spanish/portal-311.csv). Clubs host only.
   */
  language: {
    /** PROPOSED (2026-09-29): what a screen reader calls the switch */
    label: 'Language',
    english: 'English',
    spanish: 'Español',
  },
  nav: {
    approvals: 'Approvals',
    roster: 'Roster',
    redemptions: 'Redemptions',
    assignments: 'Assignments',
    groupsCodes: 'Groups & codes',
    /** Not drawn until Settings exists (Evan, 2026-09-16: when it ships, it appears). */
    settings: 'Settings',
    /** DECISIONS 84 (Mike and Evan, 16 Oct): the Club Guide on kiddiekredit.com/guides, while `guidesPage` is open. */
    clubGuide: 'Club Guide (PDF)',
  },
  shell: {
    /**
     * "Group admin · Homework Club", and "Group admin" alone when there is no group to name. Evan,
     * 2026-09-16: *"Drop the separator when the second half is empty. Same rule everywhere: a
     * dangling separator is always a bug."*
     */
    groupAdmin: (firstGroupName: string | undefined) =>
      firstGroupName ? `Group admin · ${firstGroupName}` : 'Group admin',
    orgAdmin: 'Org admin',
    /**
     * The sidebar foot. The board typesets "POWERED BY KREDIT ACADEMY · lockup artwork here" and notes it is *"typeset
     * here for layout only"* — the lockup ARTWORK goes there. So it is drawn: these two words, then the Kredit Academy
     * logo standing for the other two. "· lockup artwork here" was the designer's note, never copy, and a club
     * director saw it in production (Evan, 2026-09-16).
     */
    poweredBy: 'POWERED BY',
    /** The logo's alt text, so the line still reads "Powered by Kredit Academy" to a screen reader. */
    kreditAcademy: 'Kredit Academy',
    /** The wordmark's alt text, so the turquoise panel is not a silent image to a screen reader. */
    kiddieKredit: 'Kiddie Kredit',
  },
  approvals: {
    /** "Approvals · 9 waiting" */
    title: (n: number) => `Approvals · ${n} waiting`,
    /**
     * The 23 Oct audit (T3-6, approved by Mike 23 Oct): "Anything a club sends you, in one place." read oddly to
     * staff, who are the club. That line had replaced the board's "Homework Club · anything org-related is
     * approved here, not by parents" on 2026-09-16.
     */
    subtitle: 'Finished tasks waiting for your OK.',
    /** "Approve all 9" */
    approveAll: (n: number) => `Approve all ${n}`,
    columns: { child: 'CHILD', task: 'TASK', submitted: 'SUBMITTED', proof: 'PROOF' },
    note: 'Note ▸',
    photo: 'Photo ▸',
    /** "Reading Week · 4 of 5 reps" — the rep count is deferred (OPEN_QUESTIONS 11); only the assignment title is rendered. */
    dash: '—',
    // "5 more from yesterday · Show" is gone (the 23 Oct audit, T1-3): older rows are always on the list now, so a
    // task sent weeks ago is never hidden behind a word that says "yesterday".
  },
  roster: {
    /** "Roster · 38 children" */
    title: (n: number) => `Roster · ${n} children`,
    subtitle:
      'Enrolled by a parent, one consent per child. You see first name, avatar, age band and your own assignments.',
    searchPlaceholder: 'Search a first name',
    /**
     * Two different states, ruled final 2026-09-16 and deliberately not sharing a string: a roster
     * nobody has joined yet, and a search that matched nobody.
     */
    emptyTitle: 'No families yet',
    emptyBody: 'Families appear here when they join with your code.',
    noMatches: 'No families match that search.',
    /** "All 38" */
    chipAll: (n: number) => `All ${n}`,
    /** "Homework Club · 14" / "Reading Week · 22" */
    chipCount: (label: string, n: number) => `${label} · ${n}`,
    /** "Consent pending · 3" */
    chipPending: (n: number) => `Consent pending · ${n}`,
    columns: {
      child: 'CHILD',
      ageBand: 'AGE BAND',
      groups: 'GROUPS',
      tasksDone: 'MY TASKS DONE',
      score: 'SCORE',
      consent: 'CONSENT',
    },
    /** "Homework Club · Reading Week" */
    joinNames: (names: string[]) => names.join(' · '),
    /** "92%" */
    pct: (pct: number) => `${pct}%`,
    /** "23 of 25" */
    ofTotal: (done: number, total: number) => `${done} of ${total}`,
    notShared: 'Not shared',
    active: 'Active',
    /** "Invited · patel.family@…" — the deck truncates the address; the portal ellipsizes it with CSS. */
    invited: (email: string) => `Invited · ${email}`,
    waitingOnParent: 'Waiting on parent',
    dash: '—',
    lockNote:
      'The score band (never the number) appears only where a parent turned on “Share score with this organization”. Last names, birthdays, family chores and family rewards are never shown here. A parent can revoke consent from the app at any time; the row then disappears.',
  },
  redemptions: {
    title: 'Redemptions',
    subtitle: 'A child shows a code on their device; you look it up and mark it fulfilled.',
    /** "KK-4821" — the field's sample value doubles as its placeholder. */
    codePlaceholder: 'KK-4821',
    lookUp: 'Look up',
    /** "CODE KK-4821 · VALID" */
    codeValid: (code: string) => `CODE ${code} · VALID`,
    /** "Elijah · Pizza party" */
    resultTitle: (firstName: string, rewardTitle: string) => `${firstName} · ${rewardTitle}`,
    scoreGateMet: 'score gate met',
    markFulfilled: 'Mark fulfilled',
    notNow: 'Not now',
    /** "WAITING · 2" */
    waiting: (n: number) => `WAITING · ${n}`,
    /** "Requested today · KK-7710" / "Requested yesterday · KK-3390" */
    requested: (relativeDay: string, code: string) => `Requested ${relativeDay} · ${code}`,
    open: 'Open',
    /** "Fulfilled this month · 31 ·" */
    fulfilledThisMonth: (n: number) => `Fulfilled this month · ${n} ·`,
    exportCsv: 'Export CSV',
  },
  /** O03 · Assignment builder. "- - Add task" / "- - Add reward" are the deck's markdown artefacts for "+ Add task" / "+ Add reward" (board). */
  o03: {
    eyebrow: 'NEW ASSIGNMENT',
    /**
     * **"e.g. Summer Reading", sanctioned in COPY_DECK — and NOT the board's "Reading Week"** (Evan,
     * 2026-09-22). The old value was read off an artboard and shipped as a placeholder, with a comment
     * saying so: *"the board's sample title"*. **A copy that announces it is a copy is still a copy**, and
     * the `data-sample` guard is what found it.
     *
     * An example earns its place here: under the three-names ruling an assignment title names a PROGRAMME
     * and not a task, and an org admin has to be told that somewhere. "Summer Reading" teaches the same
     * distinction without borrowing the board's fixture.
     */
    titleSample: 'e.g. Summer Reading',
    saveDraft: 'Save draft',
    /** "Publish to 22 children" */
    publish: (n: number) => `Publish to ${childCount(n)}`,
    when: 'WHEN',
    /** "TASKS · 3" */
    tasks: (n: number) => `TASKS · ${n}`,
    addTask: '+ Add task',
    /** 03c: under the Tasks heading while chore categories are on. */
    categoryHelp: 'Each task needs a category for the Kredit Score. Amber means we couldn’t tell from the title.',
    /** 03c: the select's screen-reader label; it has no visible one (Evan, 9 Oct). */
    category: 'Category',
    /** 03c: beside Publish while it is disabled because a row is amber (Evan, 9 Oct). */
    categoryNeeded: 'Pick a category for each amber task to publish.',
    /** "5 reps" / "1 rep" */
    reps: (n: number) => (n === 1 ? '1 rep' : `${n} reps`),
    orgApproves: 'Org approves',
    parentApproves: 'Parent approves',
    rule: 'Rule: anything org-related is approved by your staff. A home-type task can hand approval to the parent. No task sizes and no Bamboo Bucks; tasks count toward the child’s Kredit Score.',
    reward: 'REWARD',
    addReward: '+ Add reward',
    name: 'NAME',
    unlocksWhen: 'UNLOCKS WHEN',
    allTasksDone: 'All tasks done',
    alsoRequires: 'Also requires a Kredit Score of at least',
    /** "· Great" — the band of the typed number (COPY_DECK's O03 sample reads "60 · Great"). */
    band: (band: string) => `· ${band}`,
    /**
     * The lowest band under the new Kredit Score (brief 05; the guides' audit, 15 Oct): "Starting", so that "Building"
     * names only the badge. Drawn in place of the wire's `Building` while `PublicConfig.scoreEngineV2` is open.
     */
    bandStarting: 'Starting',
    // DECISIONS 84 (Mike and Evan, 16 Oct): the server checks the score gate, not the child's device
    checkedOnDevice:
      'Kiddie Kredit checks the score itself, so this works even if the parent hasn’t shared the score with you.',
    assignTo: 'ASSIGN TO',
    pickChildren: 'Pick individual children',
    /** "HOW IT LOOKS TO ELIJAH" */
    preview: (firstName: string) => `HOW IT LOOKS TO ${firstName.toUpperCase()}`,
    /** "Reading Week · Ms. Dana approves" */
    previewLine: (title: string, staffName: string) => `${title} · ${staffName} approves`,
    /**
     * The 23 Oct audit (T1-7, T3-4, approved by Mike 23 Oct): the club's own short name. It said "Boys and Girls
     * Club" to every club, with the designer's "(your call)" still in it.
     */
    previewNote: (shortName: string) =>
      `Club tasks show in the child’s list with your club’s badge. Pre-readers hear “${shortName}” before the task name.`,
    /** "Mon 21 Sep" – "Fri 25 Sep": the two deck dates joined the way the brief asks for the list. */
    window: (start: string, end: string) => `${start} – ${end}`,
  },
  /** O05 · Groups & join codes (org admin). */
  /**
   * O08 · Settings. Every string is COPY_DECK's; the three helper lines exist because **nobody would guess
   * full-name versus short-form versus badge-letters from three adjacent text boxes**, and a club that
   * mistypes its own name is otherwise stuck with a wrong monogram on a child's badge shelf.
   */
  /**
   * **O06 · Quest Jr. content pipeline (super admin).** The board's labels; the SAMPLE content beside them on
   * the artboard — "ADVENTURE 2 · SAVING FOR SOMETHING BIG", Kai and the scooter, "Wants vs. needs" — is a
   * LAYOUT sample and is not transcribed here: it names an adventure retired on 2026-09-21, and the deck says
   * so in as many words. Every real string comes from `docs/handoff/quest-content/`.
   *
   * **Three panels the board draws are deliberately absent** (Evan, 2026-09-21), for three different reasons:
   * VOICE describes a render-on-publish pipeline that no longer exists — read-aloud is the device's own
   * `expo-speech` and voice picking is deferred; "Preview on device" has no transport; and AI drafting is a
   * route and a model call this milestone never scoped. A control that cannot act does not render, which is
   * the same rule as O08's em dash for the last org admin.
   */
  /**
   * **O13 · a mission's version history** (the Quest Content Engine board, docs/handoff/design/content-engine/app.js
   * `missionVM`, 2026-09-26). Every string is the board's own. "Copy into the draft" is drawn there too and is NOT
   * here yet: it needs the draft editor (content engine part 2), and a control that cannot act does not render.
   */
  o13: {
    history: 'History',
    back: 'Back to the mission',
    /** "Version 2 · live" */
    version: (n: number, live: boolean) => `Version ${n}${live ? ' · live' : ''}`,
    /** "Published by u_kk_admin · 26 Sep, 10:00" */
    publishedBy: (who: string, when: string) => `Published by ${who} · ${when}`,
    /** "Reviewed: reader by …, pre-reader by …" */
    reviewedBy: (reader?: string, prereader?: string) =>
      `Reviewed: ${[reader && `reader by ${reader}`, prereader && `pre-reader by ${prereader}`]
        .filter(Boolean)
        .join(', ')}`,
    reviewedBefore: 'Reviewed before the engine existed',
    compare: 'Compare',
    comparing: 'Comparing',
    restore: 'Restore',
    hintWithDraft:
      'Pick one version to compare it with the draft, or two to compare them with each other.',
    hint: 'Pick two versions to compare.',
    /** "COMPARE · Version 1 → version 2" / "COMPARE · Version 1 → the draft" */
    compareTitle: (a: number, b: number | 'draft') =>
      `COMPARE · Version ${a} → ${b === 'draft' ? 'the draft' : `version ${b}`}`,
    noDifferences: 'No differences.',
    audit: 'AUDIT LOG · THIS MISSION',
    copyToDraft: 'Copy into the draft',
    /** "Version 1 fails today’s gate (2 errors), so it can’t be restored as it stands. Copy it into the draft …" */
    restoreFails: (n: number, errors: number) =>
      `Version ${n} fails today\u2019s gate (${errors} ${errors === 1 ? 'error' : 'errors'}), so it can\u2019t be restored as it stands. Copy it into the draft and fix it there.`,
    /** "The draft now holds version 1. Fix what the gate flags, review, and publish." */
    copied: (n: number) =>
      `The draft now holds version ${n}. Fix what the gate flags, review, and publish.`,
    /** Flashes after a restore (app.js `restore`). */
    restored: (n: number, next: number) => `Version ${n} is live again, as version ${next}.`,
  },
  /**
   * **O11 · O12 · the content engine's mission and quiz editors** (`Quest Content Engine.dc.html`; content engine
   * part 2). Every string above the second marker is the board's own, verbatim — the prototype's `app.js` builds
   * them. The ones below it name controls the board does not draw (add, remove and reorder; "What you'll learn"; the
   * badge); Evan accepted them on 2026-09-26 and COPY_DECK records them under "O10–O12 · controls the board does not
   * draw".
   */
  o11: {
    tabs: { story: 'Story', quiz: 'Quiz', history: 'History' },
    /** O10's rail (COPY_DECK): the live content as the repo's files. */
    exportJson: 'Export JSON',
    /** Working together (Evan, 2026-09-26): one editor at a time — his words. */
    editing: (name: string) => `${name} is editing`,
    // ---- proposed 2026-09-26 (working together; the board draws none of it — listed for Evan) ----
    takeOver: 'Take over',
    claimedBy: (name: string) => `${name} has taken this to publish`,
    comments: 'Comments',
    noComments: 'No comments yet.',
    commentOn: 'On',
    commentText: 'Comment',
    addComment: 'Add comment',
    resolve: 'Resolve',
    resolved: 'Resolved',
    /** Where a comment is pinned: the line's name. */
    anchor: (anchor: string) => {
      const beat = /^beats\.(\d+)\.(prompt|spoken)$/.exec(anchor);
      if (beat) return `Beat ${Number(beat[1]) + 1} · ${beat[2] === 'prompt' ? 'words' : 'spoken'}`;
      const choice = /^beats\.(\d+)\.choices\.([AB])\.(label|consequence)$/.exec(anchor);
      if (choice) return `Beat ${Number(choice[1]) + 1} · choice ${choice[2]} · ${choice[3]}`;
      const quiz = /^quiz\.(\d+)\.prompt$/.exec(anchor);
      if (quiz) return `Question ${Number(quiz[1]) + 1}`;
      return (
        (
          {
            title: 'Title',
            concept: 'Concept',
            'scripts.reader': 'Reader script',
            'scripts.prereader': 'Pre-reader script',
          } as Record<string, string>
        )[anchor] ?? anchor
      );
    },
    library: 'LIBRARY',
    /** "ADVENTURE 1 · EARNING IT" */
    eyebrow: (order: number, title: string) => `ADVENTURE ${order} · ${title.toUpperCase()}`,
    /** "Mission 2 · What money is worth" */
    heading: (order: number, title: string) => `Mission ${order} · ${title}`,
    /** "Cub track · level “Money Basics” · the adventure’s badge is Helper" */
    trackLine: (
      track: 'cub' | 'panda' | undefined,
      level: string | undefined,
      badge: string | undefined,
    ) =>
      `${track === 'panda' ? 'Panda' : 'Cub'} track · level \u201c${level || '\u2014'}\u201d${badge ? ` · the adventure\u2019s badge is ${badge}` : ''}`,
    draftBanner:
      'Nothing here reaches a child until both scripts are reviewed and it is published.',
    missionTitle: 'MISSION TITLE',
    levelName: 'LEVEL NAME',
    mechanic: 'MECHANIC',
    mechanics: { story: 'Story', 'picture-quiz': 'Picture quiz' },
    pointsAt: 'POINTS AT',
    deepLinks: {
      none: 'None',
      chores: 'Chores',
      savingsGoal: 'Savings goal',
      kreditScore: 'Kredit Score',
    },
    /** "BEAT 2" */
    beat: (n: number) => `BEAT ${n}`,
    beatKind: (withChoice: boolean) => (withChoice ? 'Story, then a choice' : 'Story'),
    reader: 'READER · DRAWN ON C13',
    prereader: 'PRE-READER · HEARD',
    listen: 'Listen \u25b8',
    choices: 'CHOICES · DRAWN TO BOTH BANDS',
    choice: 'Choice',
    consequence: 'Consequence',
    /** "prompt 20/700 · 263pt" */
    spec: (run: string, measure: number) => `${run} · ${measure}pt`,
    /** "4 of 6 lines", "7 of 6 lines · too long", "Empty" */
    gauge: (lines: number, limit: number) =>
      lines === 0 ? 'Empty' : `${lines} of ${limit} lines${lines > limit ? ' · too long' : ''}`,
    /** "23 words · heard, never drawn" */
    heard: (words: number) => `${words} words · heard, never drawn`,
    /** "QUESTION 1" */
    question: (n: number) => `QUESTION ${n}`,
    readerQuestion: 'READER · QUESTION',
    rows: { pictures: 'All pictures', type: 'All type', mixed: 'Mixed · fix before publishing' },
    rowNotePictures: 'Captions set at caption 17/800.',
    /** "Every tile draws its answer at prompt 20/700, the size the longest caption needs." */
    rowNoteType: (run: string) =>
      `Every tile draws its answer at ${run}, the size the longest caption needs.`,
    tiles: 'TILES · 2 TO 4, ALL PICTURES OR ALL TYPE',
    /** What a tile draws, under its picture well. */
    artLine: (a: { key?: string; fromId?: boolean; chosenType?: boolean }) =>
      a.key
        ? `${a.key}${a.fromId ? ' · from the tile id' : ''}`
        : a.chosenType
          ? 'Type, chosen'
          : 'Type · the id names no icon',
    idLine: (id: string) => `id ${id}`,
    picture: 'Picture',
    answer: 'Answer',
    makeAnswer: 'Make this the answer',
    remove: 'Remove',
    addTile: '+ Add tile',
    newTileCaption: 'New answer',
    feedback: 'FEEDBACK · AFTER THE ANSWER',
    /** "Picture for “Dinner”" */
    pickerTitle: (caption: string) => `Picture for \u201c${caption}\u201d`,
    close: 'Close',
    gate: 'PUBLISH GATE',
    reviewReader: 'Reader script · 9\u201312',
    reviewPrereader: 'Pre-reader script · 4\u20138',
    /** "Reviewed by u_kk_admin · 26 Sep, 10:00" */
    reviewedBy: (by: string, when: string) => `Reviewed by ${by} · ${when}`,
    needsReview: 'Needs review',
    nothingToReview: 'Nothing to review',
    nothingToPublish: 'Nothing to publish. Edit anything and the gate runs as you type.',
    /** "Live: version 2, published by u_kk_admin · 26 Sep, 10:00" */
    liveLine: (n: number, by?: string, when?: string) =>
      by
        ? `Live: version ${n}, published by ${by} · ${when}`
        : `Live: version ${n}, seeded from the content files`,
    // the publish gate (CONTENT_ENGINE §5; COPY_DECK O11)
    /** "1 error, which blocks" / "3 errors, which block" */
    errorWord: (n: number) => (n === 1 ? 'error, which blocks' : 'errors, which block'),
    warningWord: (n: number) =>
      n === 1 ? 'warning, which doesn\u2019t' : 'warnings, which don\u2019t',
    warningsTitle: 'WARNINGS · SHOWN, NEVER BLOCKING',
    /** "1 error blocks publishing. There is no override." */
    errorsBlock: (n: number) =>
      `${n} ${n === 1 ? 'error blocks' : 'errors block'} publishing. There is no override.`,
    discarded: 'Draft discarded. The editor now shows what is live.',
    // ---- controls the board does not draw: accepted by Evan 2026-09-26, recorded in COPY_DECK (O10–O12) ----------
    save: 'Save the draft',
    saveFirst: 'Save the draft before publishing.',
    addBeat: '+ Add a beat',
    addChoices: '+ Add a choice',
    removeChoices: 'Remove the choice',
    addQuestion: '+ Add a question',
    moveUp: 'Move up',
    moveDown: 'Move down',
    feedbackWrong: 'FEEDBACK · WHEN WRONG',
    learn: 'WHAT YOU\u2019LL LEARN',
    learnApproved: 'Approved · on the trail',
    learnDraft: 'Draft · not on the trail until approved',
    approve: 'Approve',
    withdraw: 'Withdraw',
    badge: 'BADGE',
    badgeName: 'Badge name',
    missions: 'MISSIONS',
    deleteMission: 'Delete',
    // ---- proposed 2026-09-26: an adventure's own draft and publish (Evan's ruling 2); the board draws no screen for it
    adventureDraft: 'Nothing in this draft reaches a child until the adventure is published.',
    learnApprovedPending: 'Approved · goes on the trail when the adventure is published',
    discardDraft: 'Discard draft',
    /** Why a published mission offers Retire instead of Delete. */
    deleteHint:
      'Only a mission that was never published can be deleted. A published one is retired, so a child\u2019s finished run still opens.',
  },
  /**
   * **O17 · Team** — the content team at admin.kiddiekredit.com/team (Evan, 2026-09-26; COPY_DECK O17), on O08's staff
   * pattern. Remove and the em dash are O08's own.
   */
  /** O14 · Icon library (the Quest Content Engine board; COPY_DECK "O14 · Icon library"). */
  o14: {
    nav: 'Icon library',
    title: 'Icon library',
    manifest: (version: number, count: number) =>
      `Manifest version ${version} · ${count} content icons · phones cache it and fall back to the bundled set with no network`,
    inPicker: (n: number) => `In the picker · ${n}`,
    uploadedChip: (n: number) => `Uploaded · ${n}`,
    retiredChip: (n: number) => `Retired · ${n}`,
    search: 'Search pictures',
    nothing: 'Nothing matches.',
    used: (n: number) => `Used ${n}×`,
    unused: 'Unused',
    uploadedTag: 'Uploaded',
    retiredTag: 'Retired',
    bundled: 'Bundled with the app.',
    uploaded: 'Uploaded through the library.',
    retired: 'Retired: hidden from the picker, still drawn where it is already used.',
    whereUsed: 'WHERE IT IS USED',
    notUsed: 'Nothing uses it yet.',
    renameUsed:
      'A key can’t be renamed once content uses it. Renaming makes a new key plus an alias.',
    renameFree: 'Nothing uses it yet, so the key can still change.',
    retire: 'Retire',
    addTitle: 'ADD AN ICON',
    addIntro:
      'SVG in the wrapper from the Quest Jr. Art Reference: a 24 × 24 grid, 2-unit strokes, round caps and joins, no fill, three strokes at most. When a Figma or Illustrator export differs only mechanically, the validator drafts the clean-up for you.',
    choose: 'Choose an SVG file',
    meets: (n: number) =>
      `Meets the Art Reference. It is saved in the reference wrapper, and the app draws its ${n} paths.`,
    mechanical: 'Every difference is mechanical. Clean up the file, then check the result.',
    redraw: 'This needs a redraw. Fix the drawing and upload it again; nothing is saved.',
    cleanUp: 'Clean up the file',
    cleanedUp: 'Cleaned up',
    whereAppear: 'WHERE IT WILL APPEAR',
    previews: {
      tile: 'Quiz tile · 38',
      badge: 'Badge disc · 20',
      unearned: 'Unearned · 38',
      enlarged: 'Enlarged · 88',
    },
    key: 'KEY',
    keyHelp: 'Lowercase words joined by hyphens, like “dog-bowl”.',
    keyTaken: (key: string) => `“${key}” is taken. A key names one drawing.`,
    add: 'Add to library',
    cancel: 'Cancel',
    // ---- proposed 2026-09-27 (the board predates drafts: an upload now waits for a second Publisher; listed for Evan) ----
    draftTag: 'Draft',
    draftChip: (n: number) => `Drafts · ${n}`,
    draft: 'A draft: it reaches phones once another Publisher publishes it.',
    publish: 'Publish',
    savedDraft: (key: string) =>
      `“${key}” is saved as a draft. It reaches phones once another Publisher publishes it.`,
    published: (key: string, version: number) =>
      `“${key}” is live and ships to phones with manifest version ${version}.`,
    picture:
      'A filled picture. The clean-up leaves one navy shape on transparent: nothing is drawn behind it.',
  },
  /**
   * **O18, a club's application** (Evan, 2026-09-27). The headline, the fields and the thanks line are his; everything
   * marked PROPOSED is a draft in COPY_DECK O18 for him to rule.
   */
  /**
   * **Village Square, the admin** (P35's notes: "your team writes and publishes everything in the admin, with the same
   * review rules as lessons"). The board draws no admin screen, so every line here is PROPOSED (COPY_DECK "Village
   * Square · admin") for Evan to rule; the shelf names are the board's.
   */
  /** PROPOSED (2026-09-28): the admin's Help page — labels await Evan's ruling on the admin's words */
  help: {
    nav: 'Help',
    title: 'Help',
    subtitle:
      'The questions families see under Help in the app. Only published questions reach the app.',
    newQuestion: 'New question',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    publish: 'Publish',
    unpublish: 'Take out of the app',
    delete: 'Delete draft',
    status: { draft: 'Draft', live: 'In the app' } as Record<string, string>,
    fields: {
      question: 'Question',
      answer: 'Answer — a blank line between paragraphs',
      order: 'Order (lower shows first)',
    },
  },
  /** PROPOSED (21 Oct, DECISIONS 92): the admin's Club Billing page (owners only, English only like Metrics) */
  clubBilling: {
    nav: 'Club Billing',
    title: 'Club Billing',
    subtitle: 'Every club this month: what it owes, who sponsors it, and which are paused.',
    ownersOnly: 'Only an owner can read club billing.',
    empty: 'No clubs yet.',
    status: {
      none: 'No billing',
      active: 'Paying',
      past_due: 'Payment failed',
      paused: 'Paused',
      canceled: 'Canceled',
    } as Record<'none' | 'active' | 'past_due' | 'paused' | 'canceled', string>,
    plan: { monthly: 'Monthly', yearly: 'Yearly' } as Record<'monthly' | 'yearly', string>,
    comped: 'Comped',
    month: (highest: number, billable: number, dollars: string) =>
      `${highest} ${highest === 1 ? 'child' : 'children'} at most this month · ${billable} billed · about ${dollars}`,
    deletesOn: (date: string) => `Deleted on ${date} if still unpaid`,
    comp: 'Comp',
    stopComping: 'Stop Comping',
    sponsoredChildren: 'Sponsored children',
    sponsor: 'Sponsor',
    save: 'Save',
    saved: 'Saved',
  },
  /** PROPOSED (2026-09-28): the admin's Metrics page (owners only, counts only) — labels await Evan's ruling */
  metrics: {
    nav: 'Metrics',
    title: 'Metrics',
    subtitle: 'Counts across all families. Nothing here names a family or a person.',
    ownersOnly: 'Only an owner can read the metrics.',
    firstApproved: 'First approved chore',
    firstApprovedOf: 'Families with a chore approved, of all families',
    setup: 'Where new accounts stop (the last 30 days)',
    steps: {
      account: 'Signed up',
      family: 'Made a family',
      child: 'Added a child',
      chore: 'Added a chore',
      approvedChore: 'Had a chore approved',
    } as Record<string, string>,
    familyPlus: 'Family Plus',
    subscribedNow: 'Subscribed now',
    freeYearNow: 'On the free launch year now',
    startsAndCancellations: (starts: number, cancellations: number) => `${starts} started · ${cancellations} ended`,
    weeklyActive: 'Families active each week',
    weekOf: (monday: string) => `Week of ${monday}`,
    signups: 'New families each day (the last 30 days)',
    asOf: (at: string, timezone: string) => `As of ${at}; days and weeks in ${timezone} time.`,
  },
  vsq: {
    nav: 'Village Square',
    title: 'Village Square',
    subtitle:
      'Guides, printables, events and offers for grown-ups. Each goes live after a review, like a lesson.',
    kinds: {
      guide: 'Guides',
      printable: 'Printables',
      event: 'Events',
      ourProduct: 'From Kredit Academy',
      money: 'Help with your money',
      partnerOffer: 'Partner offers',
    } as Record<string, string>,
    status: { draft: 'Draft', live: 'Live', takenDown: 'Taken down' } as Record<string, string>,
    newItem: 'New item',
    edit: 'Edit',
    save: 'Save draft',
    cancel: 'Cancel',
    review: 'Approve the words',
    reviewed: 'Words approved',
    reviewStale: 'Changed since it was approved',
    partnerCard: 'Partner approved the words',
    publish: 'Publish',
    takeDown: 'Take down',
    versions: 'Versions',
    restore: 'Back to draft',
    discard: 'Discard draft',
    counts: (taps: number, yes: number, no: number) =>
      `${taps} taps · ${yes} found it helpful · ${no} didn’t`,
    fields: {
      kind: 'Shelf',
      title: 'Title',
      line: 'One line',
      provider: 'Whose it is',
      minAge: 'Youngest age',
      maxAge: 'Oldest age',
      readers: 'Reading level',
      readMinutes: 'Minutes to read',
      body: 'Text — a blank line between paragraphs; start a line with ## for a subhead',
      printable: 'Printable',
      startsAt: 'Starts at',
      familyPlusOnly: 'Family Plus members only',
      linkUrl: 'Button link (https://…; {code} for the partner’s code)',
      linkLabel: 'Button words',
      partner: 'Partner',
      /** PROPOSED (2026-09-28, P35's rows): an icon key from the library; published icons only */
      picture: 'Picture (an icon from the library)',
      /** PROPOSED (2026-09-28): how long a printable takes, optional */
      printableMinutes: 'Minutes it takes',
    },
    /** PROPOSED (2026-09-28): P35d's weekly pick, lined up here — labels await Evan's ruling on the admin's words */
    picks: {
      title: 'Weekly pick',
      help: 'One guide or printable a week, the same for every family, from each family’s Monday. With none, or the pick unpublished, families get the newest guide.',
      none: 'Nothing picked',
      newest: (title: string) => `Families get the newest guide: ${title}`,
      serves: (title: string) => `Families get: ${title}`,
      nothingLive: 'Nothing live to show yet',
    },
    readers: { prereader: 'Pre-readers', reader: 'Readers' } as Record<string, string>,
    noPartner: 'No partner',
    partners: {
      nav: 'Partners',
      title: 'Partners',
      subtitle:
        'An owner approves each partner and counsel checks its wording before any of its cards goes live. One code per partner — never per family.',
      add: 'Add partner',
      name: 'Name',
      code: 'Referral code',
      paysFee: 'Pays us a fee (shows the fee line)',
      counselChecked: 'Counsel checked the wording',
      disclosures: 'Disclosures, one per line',
      approve: 'Approve partner',
      approved: 'Approved',
      notApproved: 'Not approved',
      ownersOnly: 'Only an owner can add or change partners.',
      // the logo (2026-09-28) — PROPOSED, added to the labels file for Evan
      logo: 'Logo',
      uploadLogo: 'Upload logo (PNG or SVG)',
      approveLogo: 'Approve logo',
      logoApproved: 'Families see this logo.',
      logoWaiting: 'Families see the name until you approve this logo.',
      save: 'Save',
    },
  },
  o18: {
    headline: 'Bring Kiddie Kredit to your club',
    /** PROPOSED */
    intro: 'Tell us about your club and we\u2019ll be in touch.',
    /**
     * The 23 Oct audit (T2-14, T3-11, approved by Mike 23 Oct): the price before approval, in the website's approved
     * words, so a director does not meet "$5 per child" for the first time after they are set up.
     */
    price: '$5 per child per month, or $4 when paid yearly. You pay only for children whose families join.',
    /** After "Website", the one field a club may leave empty (T3-11). */
    optional: '(optional)',
    fields: {
      clubName: 'Club name',
      contactName: 'Contact name',
      email: 'Email',
      phone: 'Phone',
      cityState: 'City and state',
      kidsCount: 'About how many kids',
      kidsAges: 'Ages of the kids',
      orgType: 'Type of organization',
      heardFrom: 'How did you hear about us?',
      website: 'Website',
    },
    orgTypes: {
      school: 'School',
      club: 'Club',
      library: 'Library',
      faith: 'Faith group',
      other: 'Other',
    },
    /** PROPOSED */
    send: 'Send',
    /** "Thanks, Dana. We'll be in touch within a few days." */
    thanks: (name: string) => `Thanks, ${name}. We\u2019ll be in touch within a few days.`,
    /** PROPOSED: too many from one place in an hour */
    tooMany: 'Too many applications from here. Try again in an hour.',
    // the admin's Applications page — PROPOSED, all of it
    nav: 'Applications',
    title: 'Applications',
    subtitle: 'Clubs that asked to join. Approving sets the club up and emails its director.',
    empty: 'No applications yet.',
    ownersOnly: 'Only an owner can read applications.',
    status: { new: 'New', approved: 'Approved', declined: 'Declined' } as Record<
      'new' | 'approved' | 'declined',
      string
    >,
    referredBy: (village: string) => `From ${village}\u2019s invite`,
    approve: 'Approve',
    decline: 'Decline',
    declineConfirm: 'Decline this application? They get a kind email and can apply again.',
    keepIt: 'Keep it',
    approved: (club: string) => `${club} is set up, and its director has the email.`,
    declinedNote: 'Declined. It is deleted after 12 months.',
  },
  o17: {
    nav: 'Team',
    title: 'Team',
    // PROPOSED 2026-09-26: the old line ("everyone here can edit, review and publish") stopped being true with the six roles
    subtitle: 'Each role says what someone can do with Kredit Academy content.',
    email: 'Email address',
    add: 'Add to team',
    /** The six roles (Evan, 2026-09-26): the names and what each does are his words. */
    roles: {
      owner: 'Owner',
      publisher: 'Publisher',
      writer: 'Writer',
      reviewer: 'Reviewer',
      iconDesigner: 'Icon designer',
      viewer: 'Viewer',
    },
    roleHelp: {
      owner: 'Manages the team.',
      publisher: 'Reviews and publishes.',
      writer: 'Edits drafts but can’t publish.',
      reviewer: 'Reads drafts, comments and approves, but can’t edit or publish.',
      iconDesigner: 'Uploads icons only.',
      viewer: 'Sees drafts and history and changes nothing.',
    },
    /** The one scope there is (Evan): what every role covers. */
    scope: 'All Kredit Academy content',
    invited: 'Invited · waiting for them to sign in',
    /** Board O00c: a company account's first sign-in with no role. */
    askAnOwner: 'Ask an owner to add you',
    // ---- proposed 2026-09-26 (not on the board; listed for Evan) ----
    askAnOwnerBody: (email: string) =>
      `You’re signed in as ${email}. The owners have been told, and one of them will pick your role.`,
    requested: 'Asked to join · pick a role',
    endsOn: 'Access ends',
    endsOnShown: (date: string) => `Access ends ${date}`,
    clearEndsOn: 'No end date',
    companyOnly: 'Only a Kredit Academy account can be an owner or a publisher.',
    /** "Remove Mike? They lose access right away." */
    confirmRemove: (name: string) => `Remove ${name}? They lose access right away.`,
    // an invited teammate whose address is not verified confirms it by the app's code (Evan, 2026-09-26)
    confirmTitle: 'Confirm your email',
    /** "We sent a 6-digit code to pw@kk.example. Enter it to join the team." */
    confirmBody: (email: string) =>
      `We sent a 6-digit code to ${email}. Enter it to join the team.`,
    confirm: 'Confirm',
    newCode: 'Send a new code',
  },
  o06: {
    title: 'Quest Jr. content',
    subtitle:
      'Seven adventures, eighteen missions. Nothing a child sees changes until you publish it.',
    nav: 'Quest Jr. content',
    /** The dark rail says whose admin this is, because it is not any club's. */
    railTitle: 'Kiddie Kredit',
    railSubtitle: 'Super admin',
    adventures: 'ADVENTURES',
    missionsIn: 'MISSIONS IN THIS ADVENTURE',
    /** "ADVENTURE 2 · SPEND IT OR SAVE IT" — the eyebrow over the adventure being edited. */
    adventureEyebrow: (order: number, title: string) =>
      `ADVENTURE ${order} · ${title.toUpperCase()}`,
    /** "Mission 2 · Wants and needs" */
    missionEyebrow: (order: number, title: string) => `Mission ${order} · ${title}`,
    mappedConcept: 'Mapped concept',
    source: 'SOURCE · KREDIT QUEST',
    sourceChanged:
      'The source lesson changed since this was published. Nothing republishes on its own.',
    noSource: 'Written here, not imported.',
    readerScript: 'READER SCRIPT · 9–12',
    prereaderScript: 'PRE-READER SCRIPT · 4–8',
    reviewed: 'Reviewed',
    needsReview: 'Needs review',
    markReviewed: 'Mark reviewed',
    undoReview: 'Undo review',
    /** "QUIZ ITEMS · 3 OF 3" */
    quizItems: (n: number) => `QUIZ ITEMS · ${n}`,
    addQuizItem: '+ Add item',
    publish: 'Publish',
    unpublish: 'Take off the trail',
    retire: 'Retire',
    newAdventure: 'New adventure',
    newMission: 'New mission',
    sync: 'Check the source',
    /** "12 missions checked · 2 flagged" */
    syncResult: (checked: number, flagged: number) =>
      `${checked} ${checked === 1 ? 'mission' : 'missions'} checked · ${flagged} flagged`,
    picture: 'PICTURE',
    /** The picker's empty choice: honest about what a tile without a glyph actually does. */
    noPicture: 'No picture — the tile draws its answer as type',
    pickPicture: 'Pick a picture',
    searchGlyphs: 'Search pictures',
    /** Why the author cannot type a key. */
    pickerHelper:
      'Pictures come from the app\u2019s registry. A name it does not draw is refused, so a tile never quietly shows the wrong thing.',
    answerTile: 'ANSWER',
    caption: 'CAPTION',
    /** The states, as the board names them. */
    status: (s: 'empty' | 'draft' | 'review' | 'live' | 'retired') =>
      ({ empty: 'Empty', draft: 'Draft', review: 'In review', live: 'Live', retired: 'Retired' })[
        s
      ],
    /** Why Publish is absent rather than disabled — the same rule as O08. */
    cannotPublish: 'Both scripts need a review first',
    adminOnly: 'Quest Jr. content is Kiddie Kredit staff only.',
    empty: 'No adventures yet.',
    /** The status pill word, which the O03 assignment list has always reused for a draft assignment. */
    draft: 'Draft',
  },
  /**
   * **O09 · invite families (org-led enrolment).** The board's own strings, recorded in COPY_DECK the same
   * day — the deck had no O09 section when this was built, which is itself the boundary question Evan's audit
   * asks about.
   *
   * **"Preview" is drawn on the board and deliberately absent here**, the same rule as O06's three panels: the
   * preview a director wants is the rendered email, which is composed on the server, and a second rendering in
   * the portal would be a copy that can disagree with the mail actually sent.
   */
  o09: {
    title: 'Invite families',
    subtitle:
      'The parent chooses which children join and agrees on their own phone. Nothing appears on the roster until they do.',
    send: 'SEND AN INVITATION',
    emails: 'Parent emails',
    emailsHelper: 'one per line, or paste a list',
    /** The promise that makes the screen safe, stated where a director can read it. */
    noAccountHint: 'We never tell you whether an address already has a Kiddie Kredit account.',
    joining: 'Joining',
    note: 'Add a line of your own (optional)',
    /** "Send 2 invitations" */
    sendCta: (n: number) => `Send ${n} ${n === 1 ? 'invitation' : 'invitations'}`,
    sent: 'SENT',
    expiry: 'Expire after 30 days',
    resend: 'Resend',
    state: (s: 'invited' | 'joined' | 'expired') =>
      ({ invited: 'Invited', joined: 'Joined', expired: 'Expired' })[s],
    /**
     * **The explainer says what each state does NOT mean**, because "Invited" on a dashboard reads as
     * progress: a director chasing attendance needs to know that four invitations and one joined child is
     * one child.
     */
    explainer:
      'Invited means the email went out. It does not mean they opened it, and it does not mean they have an account. Only Joined puts a child on your roster.',
    empty: 'No invitations sent yet.',
    /** The 23 Oct audit (T1-6, T3-2, approved by Mike 23 Oct): it was hard-coded in the screen. */
    notAnEmail: (address: string) => `${address} isn’t an email address.`,
  },
  o08: {
    title: 'Settings',
    subtitle: 'How your club appears to families, and who can act on its behalf.',
    club: 'THE CLUB',
    fullName: 'FULL NAME',
    shortForm: 'SHORT FORM',
    badgeLetters: 'BADGE LETTERS',
    namesHelper:
      'Full name on the consent screen, short form in a parent’s settings, badge letters on a child’s badge.',
    /** "STAFF · 3" */
    staff: (n: number) => `STAFF · ${n}`,
    inviteStaff: 'Invite a staff member',
    name: 'NAME',
    role: 'ROLE',
    /**
     * Four values for two stored roles. `orgAdmin` reads "Org admin", and "· you" is appended for the
     * signed-in member because a director needs to find their own row. A `groupAdmin` who administers NO
     * group is "Staff" — a member of the club who administers nothing — which is what the fourth value on
     * the board names; the model has no third role and this invents none.
     */
    roleValue: (role: StaffRole, groups: number, you: boolean) =>
      role === 'orgAdmin'
        ? you
          ? 'Org admin · you'
          : 'Org admin'
        : groups > 0
          ? 'Group admin'
          : 'Staff',
    remove: 'Remove',
    /** The last org admin's row: an em dash, never a disabled control (board O08, P24b's rule). */
    noRemove: '—',
    removalHelper:
      'Removal ends access immediately. Chores they approved stay, and children keep their history.',
    groupsAndCodes: 'GROUPS AND JOIN CODES',
    newGroup: 'New group',
    /** "22 children" */
    children: childCount,
    /** "expires 21 Oct" — lower case, as the board draws it under the code. */
    expires: (date: string) => `expires ${date}`,
    newCode: 'New code',
  },
  o05: {
    signedInAs: 'Signed in as',
    title: 'Groups & join codes',
    subtitle:
      'A parent types the code at the front desk; they consent per child, then the child appears in the group.',
    newGroup: 'New group',
    /** "14 children" / "16 children" — the first segment of the card meta line, on its own (1508 / 1519). */
    childCount,
    /** "14 children · 2 staff · Dana Kim" / "16 children · 3 staff" */
    meta: (children: number, staff: number, names: string[]) =>
      [childCount(children), `${staff} staff`, ...(names.length ? [names.join(', ')] : [])].join(
        ' · ',
      ),
    joinCode: 'JOIN CODE',
    /** "Expires 31 Dec · 6 uses left of 20" — either half may be absent on the wire. */
    codeLine: (expires: string | undefined, left: number | undefined, max: number | undefined) =>
      [
        expires ? `Expires ${expires}` : undefined,
        left !== undefined && max !== undefined
          ? `${plural(left, 'use', 'uses')} left of ${max}`
          : undefined,
      ]
        .filter(Boolean)
        .join(' · '),
    // "Print poster" is gone until a poster exists (the 23 Oct audit, T2-13): the board's O05 does not draw it
    revoke: 'Revoke',
    noCode: 'No active code. Children can still be invited by email from the Roster.',
    generateCode: 'Generate code',
    info: {
      staff: 'Staff',
      /** The 23 Oct audit (T3-8, approved by Mike 23 Oct). */
      staffText:
        '· Invite people by email. Group admins approve tasks and hand out rewards for their groups. Org admins can do everything here.',
      branches: 'Branches',
      /** "· this org has 3 branches; …" — 3 is sample data; the count comes from OrgSummary.branches. */
      branchesText: (n: number) =>
        // DECISIONS 84 (16 Oct): there is no branch switcher, so the line no longer promises one
        `· this org has ${plural(n, 'branch', 'branches')}.`,
      codes: 'Codes',
      /** The 23 Oct audit (T3-8, approved by Mike 23 Oct): no board screen id, no "resolves" or "org card". */
      codesText:
        '· 8 characters, a set number of uses, and an end date. A parent who types it sees your club’s name before agreeing.',
    },
    /** The 23 Oct audit (T1-4, T3-1, approved by Mike 23 Oct): never the word VALIDATION under a field. */
    nameNeeded: 'Type a name for the group.',
    emailNeeded: 'Type an email address.',
    /** The two role words as the O05 info line spells them. */
    role: { groupAdmin: 'group admin', orgAdmin: 'org admin' } as const,
  },
  phone: {
    /** "Approvals · 9" */
    approvalsSegment: (n: number) => `Approvals · ${n}`,
    /** "Redemptions · 2" */
    redemptionsSegment: (n: number) => `Redemptions · ${n}`,
    /** "Elijah · Read 20 minutes" */
    cardTitle: (firstName: string, taskTitle: string) => `${firstName} · ${taskTitle}`,
    /** "4:12 pm · Reading Week · note" — the proof kind is the trailing lowercase word; none = omitted. */
    cardMeta: (parts: Array<string | undefined>) => parts.filter(Boolean).join(' · '),
    proofNote: 'note',
    proofPhoto: 'photo',
    code: 'Code',
  },
  /**
   * States the board does not draw, ruled final by Evan on 2026-09-16 (COPY_DECK "Portal states").
   * They are here rather than in placeholders.ts because they are final copy, not stand-ins.
   */
  states: {
    notStaff: {
      title: 'This account isn’t on a club',
      body: 'Kiddie Kredit’s club portal is for staff at partner organizations. If you’re a parent, everything you need is in the Kiddie Kredit app.',
      /**
       * **The line that tells a refused person how to stop being refused** (Evan, 2026-09-22). Everything above
       * says what they cannot do; someone who genuinely works at a club needs the next step, and the next step
       * is a director inviting THIS address — which is the detail that makes it actionable rather than advice.
       */
      howToGetAccess: 'If you work at a club, ask your director to invite this email address.',
      other: 'Sign in with a different account',
    },
    /**
     * **THE CONTENT ADMIN'S OWN REFUSAL** — COPY_DECK "Not a Kiddie Kredit team account, on
     * admin.kiddiekredit.com", ruled by Evan 2026-09-23 (CONTENT_ENGINE §8.1).
     *
     * **Not `notStaff` above.** That one is a person with no club at all, almost always a parent; this one is
     * a person who HAS a club and has reached Kiddie Kredit's own address. The two refusals point in opposite
     * directions, so one string could not serve both.
     *
     * **The address is named because that is the actual question.** Somebody with two accounts does not ask
     * "am I allowed?", they ask "which one am I in?" — and the answer is the thing that tells them whether to
     * sign out or to go to Clubs, which is why there are two actions rather than one.
     */
    notKkStaff: {
      title: 'This area is for the Kiddie Kredit team',
      body: (email: string) =>
        `You’re signed in as ${email}, which isn’t a Kiddie Kredit team account. If you work with a club, use clubs.kiddiekredit.com.`,
      toClubs: 'Go to Clubs',
      signOut: 'Sign Out',
    },
    signInFailed: {
      title: 'We couldn’t sign you in',
      body: 'Something went wrong on our side, not yours. Try again, and if it keeps happening email support@kreditacademy.com.',
      cta: 'Try again',
    },
    /** Expired, already used, withdrawn, or a link so mangled the API cannot find it. No CTA: there is nothing this person can do from here. */
    inviteExpired: {
      title: 'This invite has expired',
      body: 'Invites last 7 days. Ask whoever invited you to send a new one.',
    },
    /**
     * A link with no token at all (the 23 Oct audit, T2-15, T3-7, approved by Mike 23 Oct). It used to say the
     * invite had expired, which was not true and left a dead end. The button is `signIn.cta`.
     */
    inviteIncomplete: 'This link isn’t complete. Open the invite from your email again.',
    /**
     * The right link, the wrong sign-in. Ruled 2026-09-16, replacing this session's placeholder.
     * **It names the signed-in address and never the invited one** (Evan): they own the address they
     * are signed in as, so telling them is safe; telling them which address the invite went to would
     * answer a question about somebody else.
     */
    inviteWrongAccount: {
      title: 'This invite is for someone else',
      body: (currentEmail: string) =>
        `You’re signed in as ${currentEmail}. Sign out and open the link again with the account it was sent to.`,
      cta: 'Sign out',
    },
  },
  /**
   * What a failed read or write says on screen — approved by Evan on 2026-09-16, so these are deck
   * copy and not placeholders any more. **Never the code**: a staff member who lost their session was
   * being shown the word "UNAUTHORIZED".
   *
   * Two of them do work beyond naming the failure. FORBIDDEN names the ROLE and who to ask, because
   * "you don't have access" leaves a group admin with nowhere to go. INVALID_STATE names the CAUSE,
   * because a colleague approving the same completion first is the common case and it is not an
   * error, it is a race.
   */
  errors: {
    UNAUTHORIZED: 'Your session has ended. Sign in again.',
    forbidden: (orgName: string) =>
      orgName
        ? `That’s an org-admin page. Ask an org admin at ${orgName} if you need access.`
        : // no club to name — drop the clause rather than leave "at " hanging, the same rule as
          // every other dangling half in this app
          'That’s an org-admin page. Ask an org admin if you need access.',
    NOT_FOUND: 'That’s gone.',
    VALIDATION: 'Check what you entered and try again.',
    INVALID_STATE: 'Someone else got there first. This was already handled.',
    RATE_LIMITED: 'Too many tries. Wait a moment, then try again.',
    NETWORK: 'Can’t reach Kiddie Kredit. Check your connection.',
    NOT_IMPLEMENTED: 'That is not switched on yet.',
    INVALID_CREDENTIALS: 'That sign-in did not work.',
    /** Everything else, SERVER included — the sign-in failure line without its CTA sentence. */
    other: 'Something went wrong on our side, not yours.',
  },
  signIn: {
    /** P06 */
    cta: 'Sign in',
    /** P02 field label (P06 draws only sample values). */
    email: 'Email',
    /**
     * **The sign-in screen's own frame and card** (Evan, 2026-09-22; not in COPY_DECK, which has no O-screen
     * for signed-out staff — recorded here as his, the same way the twelve board-sourced screens are).
     *
     * The screen said nothing about whose portal it was: one card in empty space. These say it.
     */
    panelHeadline: 'Kiddie Kredit for clubs',
    panelBody:
      "Give the kids in your program activities to do and rewards to earn, with their parents' consent.",
    eyebrow: 'STAFF SIGN IN',
    heading: 'Sign in to your club',
    body: 'Use the email address your club invited.',
    /** The entry CTA. "Continue" rather than "Sign in", because Auth0 decides which of the two it is. */
    continue: 'Continue',
    noClubQuestion: 'Work at a club that isn’t on Kiddie Kredit yet?',
    noClubLink: 'Get in touch',
    noClubMailto: 'mailto:support@kreditacademy.com',
    parentQuestion: 'Are you a parent?',
    parentLink: 'Get the app',
    /**
     * **Our own sign-in, email first** (board O00, O00b, O00c, O00d; Evan 2026-09-26). No Auth0 page, name or email on
     * screen: one field and Continue; a company address goes to Google, any other gets a code.
     */
    emailAddress: 'Email address',
    /** O00 / O00b only, under Continue. The admin has no Google button. */
    orGoogle: 'Or continue with Google',
    /** A personal Google account picked where a company one was asked for (O00c). */
    notCompanyAccount: 'Use your Kredit Academy account.',
    /** O00c · admin.kiddiekredit.com: O00 with admin wording, no card stack, no club or parent sections. */
    admin: {
      panelLabel: 'KIDDIE KREDIT ADMIN',
      panelHeadline: 'The team’s tools',
      eyebrow: 'CONTENT TEAM',
      heading: 'Sign in to the content editor',
      body: 'Your Kredit Academy address signs in with Google. Anyone else gets an email code.',
      tabTitle: 'Sign in · Kiddie Kredit admin',
    },
    /** O00d · the email code, either site. */
    code: {
      title: 'Check your email',
      /** Followed by the address, drawn in bold: "We sent a 6-digit code to priya@homeworkclub.org". */
      sentTo: 'We sent a 6-digit code to',
      cta: 'Sign in',
      differentEmail: 'Use a different email',
      /** "Send a new code in 0:42" */
      resendIn: (seconds: number) =>
        `Send a new code in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
      resend: 'Send a new code',
      /** P06g's dead-code line (Evan, 2026-09-25): expired, or five wrong tries. */
      dead: 'That code no longer works. Send a new code.',
      // ---- proposed 2026-09-26 (not on the board; listed for Evan) ----
      /** PROPOSED: a wrong code, under the boxes (the app's PIN-pad line, which is interim there too). */
      wrong: 'That code isn’t right. Check the email and try again.',
    },
    // ---- proposed 2026-09-26 (not on the board; listed for Evan) ----
    /** PROPOSED: publishing more than 12 hours after the sign-in (403 `signInAgain`). */
    signInAgainToPublish:
      'It’s been more than 12 hours since you signed in. Sign in again to publish.',
    /** PROPOSED: two people to put anything live — the author pressed Publish while another Publisher could. */
    secondPublisher: 'Another Publisher publishes this. You made the change.',
    /** PROPOSED: the emergency take-down's one question. */
    confirmTakeDown: (title: string) =>
      `Take ${title} off the trail now? Children stop seeing it at once.`,
  },
  /**
   * **Where a parent is sent from a web page.** The one-link routes to whichever store fits the phone; the two
   * store URLs are for a reader who cannot install anything on the device they are holding.
   */
  getTheApp: {
    cta: 'Get the Kiddie Kredit app',
    oneLink: 'https://link.kreditacademy.com/kiddiekredit',
    apple: 'Download on the App Store',
    appleUrl: 'https://apps.apple.com/app/id1440358106',
    google: 'Get it on Google Play',
    googleUrl: 'https://play.google.com/store/apps/details?id=com.kiddiekredit.app',
  },
  /** SPEC P07 deny chips (the parent app uses the same labels); mapped to DenyReason. */
  denyReasons: [
    { reason: 'try_again', label: 'Try again' },
    { reason: 'not_finished', label: 'Not finished' },
    { reason: 'wrong_day', label: 'Wrong day' },
  ] as const,
} as const;
